/**
 * Admin → Licenses
 *
 * Phase 5b item 7 (2026-05-16). Lists every license the tenant holds
 * across the FOCTTA product family (foctta_platform / discovery /
 * activity / ddr) with tier · status · limits · features · expiry.
 *
 * Read-only - license issuance + UAT → prod promotion happens via the
 * platform Super Admin surface (separate path, not in this repo).
 *
 * Backend:
 *   GET /v1/foundational/licenses → { licenses: [...] }
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  Loader2,
  AlertTriangle,
  RefreshCw,
  CheckCircle2,
  Clock,
  XCircle,
  Ban,
  Award,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listLicenses,
  type FoundationalLicense,
  type FoundationalLicenseStatus,
  type FoundationalLicenseTier,
} from '@/lib/api';

const PRODUCT_LABELS: Record<FoundationalLicense['product'], string> = {
  foctta_platform: 'FOCTTA Privacy Core',
  discovery: 'Discovery (DSPM)',
  activity: 'Activity (DAM)',
  ddr: 'Detection (DDR)',
};

const STATUS_VARIANT: Record<FoundationalLicenseStatus, 'success' | 'warning' | 'critical' | 'glass'> = {
  active: 'success',
  trial: 'warning',
  expired: 'critical',
  revoked: 'critical',
};

const STATUS_ICON: Record<FoundationalLicenseStatus, typeof CheckCircle2> = {
  active: CheckCircle2,
  trial: Clock,
  expired: XCircle,
  revoked: Ban,
};

const TIER_LABELS: Record<FoundationalLicenseTier, string> = {
  lite: 'Lite',
  standard: 'Standard',
  sovereign: 'Sovereign',
};

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.round((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

export function AdminLicenses(): JSX.Element {
  const [licenses, setLicenses] = useState<FoundationalLicense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await listLicenses();
      setLicenses(r.licenses);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load licenses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const stats = useMemo(() => {
    const active = licenses.filter((l) => l.status === 'active').length;
    const trial = licenses.filter((l) => l.status === 'trial').length;
    const expiringSoon = licenses.filter((l) => {
      if (l.status !== 'active') return false;
      const d = daysUntil(l.expiresAt);
      return d != null && d >= 0 && d <= 60;
    }).length;
    return { active, trial, expiringSoon };
  }, [licenses]);

  return (
    <PageContainer>
      <PageHeader
        title="Licenses"
        subtitle="Every license this tenant holds across the FOCTTA product family. UAT → prod promotion is a platform-Super-Admin action."
        badges={[
          { label: 'FOCTTA · 4 products', variant: 'glass' },
          { label: 'Lite · Standard · Sovereign', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {error && (
        <ErrorState
          title="Failed to load licenses"
          detail={error}
          onRetry={() => void refresh()}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Total licenses" value={licenses.length} icon={Award} hint="all statuses" />
        <KpiCard label="Active" value={stats.active} icon={CheckCircle2} glow="high" hint="production-grade" />
        <KpiCard label="Trial" value={stats.trial} icon={Clock} glow={stats.trial > 0 ? 'medium' : 'low'} hint="time-limited" />
        <KpiCard
          label="Expiring ≤ 60 days"
          value={stats.expiringSoon}
          icon={AlertTriangle}
          glow={stats.expiringSoon > 0 ? 'critical' : 'low'}
          hint="needs renewal"
        />
      </div>

      {loading && <LoadingState caption="Loading licenses…" />}

      {!loading && licenses.length === 0 && (
        <GlassCard className="p-8 text-center text-sm text-muted-foreground">
          <Award className="h-8 w-8 mx-auto mb-3 opacity-40" />
          <div className="font-medium text-foreground/90">No licenses on file</div>
          <div className="mt-1">
            License rows are issued by the platform Super Admin. If you expect to see one here and don&rsquo;t,
            contact your FOCTTA account team.
          </div>
        </GlassCard>
      )}

      {!loading && licenses.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {licenses.map((l, i) => {
            const StatusIcon = STATUS_ICON[l.status];
            const days = daysUntil(l.expiresAt);
            return (
              <motion.div
                key={l.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
              >
                <GlassCard className="p-5">
                  <CardHeader className="p-0 mb-3 flex flex-row items-start justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {PRODUCT_LABELS[l.product] ?? l.product}
                        <Badge variant="glass" className="text-[10px]">{TIER_LABELS[l.tier] ?? l.tier}</Badge>
                      </CardTitle>
                      <CardDescription>
                        Activated {new Date(l.activatedAt).toLocaleDateString()}
                        {l.expiresAt && (
                          <>
                            {' '}· expires {new Date(l.expiresAt).toLocaleDateString()}
                            {days != null && days >= 0 && (
                              <> ({days} day{days === 1 ? '' : 's'})</>
                            )}
                            {days != null && days < 0 && (
                              <> ({Math.abs(days)} day{Math.abs(days) === 1 ? '' : 's'} ago)</>
                            )}
                          </>
                        )}
                      </CardDescription>
                    </div>
                    <Badge variant={STATUS_VARIANT[l.status]} className="text-[10px] inline-flex items-center gap-1 shrink-0">
                      <StatusIcon className="h-3 w-3" />
                      {l.status}
                    </Badge>
                  </CardHeader>

                  {Object.keys(l.limits).length > 0 && (
                    <div className="mb-3">
                      <div className="text-[11px] text-muted-foreground mb-1.5">Limits</div>
                      <div className="grid grid-cols-2 gap-1.5 text-xs">
                        {Object.entries(l.limits).map(([k, v]) => (
                          <div key={k} className="flex items-center justify-between glass-lite rounded px-2 py-1">
                            <code className="font-mono text-[10px] truncate">{k}</code>
                            <span className="tabular shrink-0 ml-1">
                              {v == null ? <span className="italic text-muted-foreground">∞</span> : v.toLocaleString('en-IN')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {Object.keys(l.features).length > 0 && (
                    <div>
                      <div className="text-[11px] text-muted-foreground mb-1.5">Features</div>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(l.features).map(([k, v]) => (
                          <Badge
                            key={k}
                            variant={v ? 'success' : 'glass'}
                            className="text-[10px] font-mono"
                            title={v ? 'enabled' : 'disabled'}
                          >
                            {v ? '✓' : '○'} {k}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {l.procurementRef && (
                    <div className="mt-3 pt-3 border-t border-border/20 text-[11px] text-muted-foreground">
                      Procurement ref: <code className="font-mono text-foreground/80">{l.procurementRef}</code>
                    </div>
                  )}
                  {l.revocationReason && (
                    <div className="mt-3 pt-3 border-t border-sev-critical/40 text-[11px] text-sev-critical">
                      Revoked: {l.revocationReason}
                    </div>
                  )}
                </GlassCard>
              </motion.div>
            );
          })}
        </div>
      )}
    </PageContainer>
  );
}
